import React, { useState, useRef, useEffect, forwardRef } from 'react';
import ivclass from '@invideoio/web-shared/Modules/IVClass';
import { PlatformType } from '@invideoio/web-shared/constants/common';
import PlayPauseIcon from '@components/common/VideoComponent/PlayPauseIcon';

interface VideoComponentWithPlayControlsProps
  extends React.HTMLProps<HTMLVideoElement> {
  platform?: PlatformType;
  children: React.ReactNode;
  autoPlay?: boolean;
  loop?: boolean;
  muted?: boolean;
  poster?: string;
  className?: string;
  width?: string;
  height?: string;
  loading?: 'preload' | 'eager' | 'lazy';
}

const VideoComponentWithPlayControls = forwardRef<
  HTMLVideoElement,
  VideoComponentWithPlayControlsProps
>((props, ref) => {
  const {
    platform,
    children,
    autoPlay = false,
    loading,
    ...otherProps
  } = props;

  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(autoPlay || false);
  const [showControls, setShowControls] = useState(true);
  const controlsTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Handle forwarded ref
  useEffect(() => {
    if (ref) {
      if (typeof ref === 'function') {
        ref(videoRef.current);
      } else {
        ref.current = videoRef.current;
      }
    }
  }, [ref]);

  const clearControlsTimeout = () => {
    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current);
      controlsTimeoutRef.current = null;
    }
  };

  const hideControlsAfterDelay = () => {
    clearControlsTimeout();
    controlsTimeoutRef.current = setTimeout(() => {
      setShowControls(false);
    }, 1000);
  };

  const togglePlayPause = () => {
    const video = videoRef.current;
    if (!video) return;

    if (video.paused) {
      video
        .play()
        .then(() => {
          setIsPlaying(true);
          hideControlsAfterDelay();
        })
        .catch(error => {
          console.error('Error playing video:', error);
        });
    } else {
      video.pause();
      setIsPlaying(false);
      setShowControls(true);
      clearControlsTimeout();
    }
  };

  const handleVideoClick = () => {
    togglePlayPause();
    // Always show controls when video is clicked
    setShowControls(true);
  };

  const handleMouseEnter = () => {
    setShowControls(true);
    clearControlsTimeout();
  };

  const handleMouseLeave = () => {
    if (isPlaying) {
      hideControlsAfterDelay();
    }
  };

  // Set up event listeners
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handlePlay = () => {
      setIsPlaying(true);
      hideControlsAfterDelay();
    };

    const handlePause = () => {
      setIsPlaying(false);
      setShowControls(true);
    };

    video.addEventListener('play', handlePlay);
    video.addEventListener('pause', handlePause);

    return () => {
      video.removeEventListener('play', handlePlay);
      video.removeEventListener('pause', handlePause);
      clearControlsTimeout();
    };
  }, []);

  // Handle initial autoplay if set
  useEffect(() => {
    if (autoPlay && videoRef.current) {
      videoRef.current
        .play()
        .then(() => {
          setIsPlaying(true);
          hideControlsAfterDelay();
        })
        .catch(error => {
          console.error('Error auto-playing video:', error);
        });
    }
  }, [autoPlay]);

  // Preload video if specified
  if (loading === 'preload' && otherProps.src) {
    return (
      <>
        <link rel="preload" as="video" href={otherProps.src as string} />
        <VideoContent
          videoRef={videoRef}
          isPlaying={isPlaying}
          showControls={showControls}
          handleVideoClick={handleVideoClick}
          handleMouseEnter={handleMouseEnter}
          handleMouseLeave={handleMouseLeave}
          togglePlayPause={togglePlayPause}
          otherProps={otherProps}
        >
          {children}
        </VideoContent>
      </>
    );
  }

  return (
    <VideoContent
      videoRef={videoRef}
      isPlaying={isPlaying}
      showControls={showControls}
      handleVideoClick={handleVideoClick}
      handleMouseEnter={handleMouseEnter}
      handleMouseLeave={handleMouseLeave}
      togglePlayPause={togglePlayPause}
      otherProps={otherProps}
    >
      {children}
    </VideoContent>
  );
});

interface VideoContentProps {
  videoRef: React.RefObject<HTMLVideoElement>;
  isPlaying: boolean;
  showControls: boolean;
  handleVideoClick: () => void;
  handleMouseEnter: () => void;
  handleMouseLeave: () => void;
  togglePlayPause: () => void;
  otherProps: any;
  children: React.ReactNode;
}

const VideoContent = ({
  videoRef,
  isPlaying,
  showControls,
  handleVideoClick,
  handleMouseEnter,
  handleMouseLeave,
  togglePlayPause,
  otherProps,
  children,
}: VideoContentProps) => {
  return (
    <div
      className={ivclass('iv-relative iv-w-full iv-h-full iv-overflow-hidden')}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <video
        ref={videoRef}
        className={ivclass(
          otherProps.className || '',
          'iv-w-full iv-h-full iv-object-cover iv-cursor-pointer',
        )}
        onClick={handleVideoClick}
        playsInline
        webkit-playsinline="true"
        disablePictureInPicture
        {...otherProps}
      >
        {children}
      </video>

      <button
        onClick={togglePlayPause}
        className={ivclass(
          'iv-absolute iv-top-1/2 iv-left-1/2 iv-transform iv--translate-x-1/2 iv--translate-y-1/2',
          'iv-bg-grey-0',
          'iv-border-solid iv-border-grey-85',
          'iv-rounded-full',
          'iv-w-[90px] iv-h-[90px] sm:iv-w-[65px] sm:iv-h-[65px]',
          'iv-flex iv-items-center iv-justify-center',
          'iv-shadow-lg',
          'iv-cursor-pointer',
          'iv-transition-all iv-duration-300 iv-ease-in-out',
          showControls
            ? 'iv-opacity-70 hover:iv-opacity-100 hover:iv-scale-110'
            : 'iv-opacity-0',
        )}
        style={{
          border: '5px solid rgba(133, 133, 133, 0.3)',
          zIndex: 10,
        }}
      >
        <PlayPauseIcon isPlaying={isPlaying} size={40} />
      </button>
    </div>
  );
};

VideoComponentWithPlayControls.displayName = 'VideoComponentWithPlayControls';

export default VideoComponentWithPlayControls;
